import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FiFileText } from "react-icons/fi";

import HideLegacyFooter from "../../../../shared/components/HideLegacyFooter";
import useApi from "../../../../shared/hooks/useApi";
import DocumentReviewModal from "../../../document-review/DocumentReviewModal";
import { PatientTaskInstanceId } from "../../../../shared/schema/schema";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { TASKS_V2_REGULAR_QUERY_KEY } from "../actions/documentActions";
import { useTasksV2OpenReview, useTasksV2OpenTask } from "../hooks/useTasksV2Filters";
import { GET_PATIENT_TASK_REGULAR_QUERY } from "../tasks-v2.graphql";
import { DocBucket, countDocsByBucket, deriveDocStatus } from "./docStatus";
import TasksV2DocumentActions from "./TasksV2DocumentActions";
import { StatusPill, TasksV2StatusBadge } from "./TasksV2StatusBadge";

const BOOTSTRAP_MODAL_BACKDROP_Z_INDEX = 1040;

const BUCKETS: { bucket: DocBucket; label: string; color: string }[] = [
  { bucket: "approved", label: "Approved", color: "blue" },
  { bucket: "completed", label: "Completed", color: "green" },
  { bucket: "missing", label: "Missing", color: "red" },
];

function formatFullName(
  p: { firstName: string; middleName?: string | null; lastName: string } | null | undefined
): string {
  if (!p) return "—";
  return [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ") || "—";
}

function usePatientTaskRegular(id: PatientTaskInstanceId) {
  const { api } = useApi();

  return useQuery({
    // Under the list's key, so the document actions that refresh the list refresh this too.
    queryKey: [TASKS_V2_REGULAR_QUERY_KEY, "byId", id],
    queryFn: () => api.graphql(GET_PATIENT_TASK_REGULAR_QUERY, { variables: { id } }),
    select: (data) => data.patientTaskRegulars.nodes.at(0) ?? null,
  });
}

/** The documents of one task, opened from its row in the list (or a link carrying its id). */
export default function TasksV2TaskDrawer() {
  const { taskInstanceId, closeTask } = useTasksV2OpenTask();
  const { reviewDocumentTypeId, closeReview } = useTasksV2OpenReview();

  // "Review form" opens the AngularJS (Bootstrap) modal, which sits at z-index 1040-1050. The
  // drawer goes just under it, level with the AngularJS navbars, and leaves focus free so the
  // modal's fields can be typed in while the drawer stays open behind it.
  // The review of one of its documents opens full screen over the drawer (same layer, later in
  // the DOM), also driven by the URL. It is rendered inside the drawer's content on purpose: the
  // drawer's scroll lock only lets through wheel/touch events that bubble (in the React tree)
  // through its content, so a review mounted beside the drawer could not be scrolled.
  // While the review is open the drawer leaves hiding the rest of the page from assistive tech to
  // the review: when both open at once (a link), the drawer would otherwise hide the review too.
  const isReviewOpen = taskInstanceId !== null && reviewDocumentTypeId !== null;
  return (
    <Drawer
      isOpen={taskInstanceId !== null}
      placement="right"
      size="lg"
      trapFocus={false}
      useInert={!isReviewOpen}
      onClose={closeTask}
    >
      {taskInstanceId !== null && <HideLegacyFooter />}
      <DrawerOverlay bg="blackAlpha.400" zIndex={BOOTSTRAP_MODAL_BACKDROP_Z_INDEX - 1} />
      <DrawerContent bg="gray.50" containerProps={{ zIndex: BOOTSTRAP_MODAL_BACKDROP_Z_INDEX - 1 }}>
        <DrawerCloseButton borderRadius="full" right={4} top={4} />
        {taskInstanceId !== null && <TaskDrawerContent taskInstanceId={taskInstanceId} />}
        {isReviewOpen && (
          <DocumentReviewModal
            // A new document (e.g. a direct URL change) starts a fresh review session
            key={`${taskInstanceId}-${reviewDocumentTypeId}`}
            documentTypeId={reviewDocumentTypeId}
            isOpen={true}
            taskInstanceId={taskInstanceId}
            onClose={closeReview}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}

function MetaItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box minW={0}>
      <Text
        color="gray.500"
        fontSize="11px"
        fontWeight="semibold"
        letterSpacing="wider"
        textTransform="uppercase"
      >
        {label}
      </Text>
      <Text color="gray.800" fontSize="sm" fontWeight="medium" mt={0.5} noOfLines={1}>
        {children}
      </Text>
    </Box>
  );
}

function DocumentsProgress({
  counts,
  total,
}: {
  counts: Record<DocBucket, number>;
  total: number;
}) {
  return (
    <Box>
      <SimpleGrid columns={3} spacing={3}>
        {BUCKETS.map(({ bucket, label, color }) => (
          <Box
            key={bucket}
            bg="white"
            borderColor="gray.200"
            borderRadius="lg"
            borderWidth={1}
            px={4}
            py={3}
          >
            <Text color={`${color}.500`} fontSize="2xl" fontWeight="semibold" lineHeight="short">
              {counts[bucket]}
            </Text>
            <Text color="gray.500" fontSize="xs" fontWeight="medium">
              {label}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
      <Flex bg="gray.200" borderRadius="full" gap="2px" h="6px" mt={3} overflow="hidden">
        {BUCKETS.filter(({ bucket }) => counts[bucket] > 0).map(({ bucket, label, color }) => (
          <Tooltip key={bucket} hasArrow label={`${counts[bucket]} ${label.toLowerCase()}`}>
            <Box bg={`${color}.400`} flex={counts[bucket] / total} h="full" />
          </Tooltip>
        ))}
      </Flex>
    </Box>
  );
}

function TaskDrawerContent({ taskInstanceId }: { taskInstanceId: PatientTaskInstanceId }) {
  const taskQuery = usePatientTaskRegular(taskInstanceId);

  switch (taskQuery.status) {
    case "pending":
      return (
        <>
          <DrawerHeader bg="white" borderBottomWidth={1} py={5}>
            <Skeleton borderRadius="md" h={7} w="50%" />
            <Skeleton borderRadius="md" h={10} mt={4} />
          </DrawerHeader>
          <DrawerBody py={5}>
            <Stack spacing={3}>
              <Skeleton borderRadius="lg" h="72px" />
              <Skeleton borderRadius="lg" h="64px" />
              <Skeleton borderRadius="lg" h="64px" />
            </Stack>
          </DrawerBody>
        </>
      );
    case "error":
      return (
        <>
          <DrawerHeader bg="white" borderBottomWidth={1}>
            Task
          </DrawerHeader>
          <DrawerBody py={5}>
            <Text color="red.500" mb={3}>
              Failed to load the task.
            </Text>
            <Button size="sm" onClick={() => taskQuery.refetch()}>
              Retry
            </Button>
          </DrawerBody>
        </>
      );
    case "success":
      break;
  }

  const task = taskQuery.data;

  if (task === null) {
    return (
      <>
        <DrawerHeader bg="white" borderBottomWidth={1}>
          Task
        </DrawerHeader>
        <DrawerBody py={5}>
          <Text color="gray.500">This task could not be found.</Text>
        </DrawerBody>
      </>
    );
  }

  const patientName = formatFullName(task.patient);
  const caregiverName = task.caregiver ? formatFullName(task.caregiver) : null;
  const documents = task.documents.nodes;
  const counts = countDocsByBucket(documents);
  const status =
    task.status === "COMPLETED" || task.status === "INCOMPLETED" || task.status === "APPROVED"
      ? task.status
      : null;

  return (
    <>
      <DrawerHeader bg="white" borderBottomWidth={1} pb={5} pr={14} pt={5}>
        <Flex align="center" gap={3} wrap="wrap">
          <Heading fontWeight="semibold" size="md">
            {task.taskName}
          </Heading>
          <TasksV2StatusBadge status={status} />
        </Flex>
        <SimpleGrid columns={{ base: 2, md: 4 }} mt={4} spacing={4}>
          <MetaItem label="Patient">{patientName}</MetaItem>
          <MetaItem label="Caregiver">{caregiverName ?? "—"}</MetaItem>
          <MetaItem label="Scheduled">
            {task.scheduledDate ? dateFormatter.toDate(task.scheduledDate) : "—"}
          </MetaItem>
          <MetaItem label="Contract">{task.contract?.contractType?.name ?? "—"}</MetaItem>
        </SimpleGrid>
      </DrawerHeader>
      <DrawerBody px={6} py={5}>
        <Flex align="baseline" justify="space-between" mb={3}>
          <Heading fontWeight="semibold" size="sm">
            Documents
          </Heading>
          <Text color="gray.500" fontSize="sm">
            {documents.length} total
          </Text>
        </Flex>
        {documents.length === 0 ? (
          <Flex
            align="center"
            bg="white"
            borderColor="gray.200"
            borderRadius="lg"
            borderStyle="dashed"
            borderWidth={1}
            direction="column"
            gap={2}
            py={10}
          >
            <Icon as={FiFileText} boxSize={6} color="gray.300" />
            <Text color="gray.500" fontSize="sm">
              This task has no documents.
            </Text>
          </Flex>
        ) : (
          <Stack spacing={4}>
            <DocumentsProgress counts={counts} total={documents.length} />
            <Stack spacing={2}>
              {documents.map((doc) => {
                const docStatus = deriveDocStatus(doc);
                const approvedByName = doc.approved ? doc.approvedByName : null;

                return (
                  <Flex
                    key={String(doc.documentTypeId)}
                    _hover={{ borderColor: "gray.300", boxShadow: "sm" }}
                    align="flex-start"
                    bg="white"
                    borderColor="gray.200"
                    borderRadius="lg"
                    borderWidth={1}
                    gap={3}
                    px={4}
                    py={3}
                    transition="border-color 0.15s, box-shadow 0.15s"
                  >
                    <Flex
                      align="center"
                      bg={`${docStatus.colorScheme}.50`}
                      borderRadius="md"
                      color={`${docStatus.colorScheme}.500`}
                      flexShrink={0}
                      h={9}
                      justify="center"
                      w={9}
                    >
                      <Icon as={FiFileText} boxSize={4} />
                    </Flex>
                    <Box flex={1} minW={0}>
                      <Flex align="center" gap={2} wrap="wrap">
                        <Text color="gray.800" fontSize="sm" fontWeight="medium">
                          {doc.title}
                        </Text>
                        <StatusPill colorScheme={docStatus.colorScheme} label={docStatus.label} />
                      </Flex>
                      {approvedByName !== null && (
                        <Text color="gray.500" fontSize="xs" mt={1}>
                          Approved by {approvedByName}
                        </Text>
                      )}
                      {doc.reopenHistory.nodes.map((entry, i) => (
                        <Text key={i} color="gray.500" fontSize="xs" mt={1}>
                          Reopened {dateFormatter.toDate(entry.reopenedAt)} by{" "}
                          {entry.reopenedByName ?? "Unknown"} — “{entry.reason}”
                        </Text>
                      ))}
                      <Box borderColor="gray.100" borderTopWidth={1} ml={-2} mt={2.5} pt={2}>
                        <TasksV2DocumentActions doc={doc} taskInstanceId={task.id} />
                      </Box>
                    </Box>
                  </Flex>
                );
              })}
            </Stack>
          </Stack>
        )}
      </DrawerBody>
    </>
  );
}
